import { CircleAlert } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { Input as InputPrimitive } from '../primitives/input.tsx';
import { Label } from '../primitives/label.tsx';
import { Popover, PopoverAnchor, PopoverContent } from '../primitives/popover.tsx';
import { Select as SelectRoot, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../primitives/select.tsx';
import { Textarea as TextareaPrimitive } from '../primitives/textarea.tsx';

export interface FieldProps {
  /** Visible label above the control. */
  label?: ReactNode;
  /** One line under the control: what to type, or a constraint. */
  hint?: ReactNode;
  /** Inline validation message that says how to fix it; marks the control invalid and replaces the hint. */
  error?: ReactNode;
  className?: string;
  /** The control's id when the caller sets one; the label points at it. */
  id?: string;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/**
 * Label above, control, help text below; the error replaces the help text (components.md,
 * Forms). Wired for screen readers. Used by Input, Textarea and Select.
 */
export function Field({ label, hint, error, className, id: idProp, children }: FieldProps) {
  const auto = useId();
  const id = idProp || auto;
  const msgId = `${auto}-msg`;
  const hasMsg = error != null || hint != null;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label != null && <Label htmlFor={id}>{label}</Label>}
      {children({ id, describedBy: hasMsg ? msgId : undefined, invalid: error != null })}
      {hasMsg && (
        <p id={msgId} className={cn('flex items-start gap-1.5 text-caption', error != null ? 'text-status-danger' : 'text-fg-subtle')}>
          {error != null && <CircleAlert className="mt-0.5 size-3 shrink-0" aria-hidden />}
          <span className="min-w-0">{error ?? hint}</span>
        </p>
      )}
    </div>
  );
}

export interface InputProps extends Omit<ComponentProps<'input'>, 'size' | 'list'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Leading icon inside the field. */
  icon?: ReactNode;
  /** Trailing element inside the field (a Kbd hint, a clear button). */
  trailing?: ReactNode;
  size?: 'sm' | 'md';
  wrapperClassName?: string;
  /**
   * Known values offered as the person types, in a styled list above any dialog. Free text
   * stays allowed. Replaces a native datalist, whose popup the browser draws unstyled.
   */
  suggestions?: readonly string[];
}

/** The most suggestions the list shows at once. */
const SUGGESTION_LIMIT = 8;

/** Suggestions that contain what was typed (any case), in their order, without the exact value. */
export function matchSuggestions(all: readonly string[], typed: string, limit = SUGGESTION_LIMIT): string[] {
  const q = typed.trim().toLowerCase();
  const out: string[] = [];
  for (const s of new Set(all)) {
    if (out.length >= limit) break;
    const v = s.toLowerCase();
    if (v === q || (q && !v.includes(q))) continue;
    out.push(s);
  }
  return out;
}

/** Set an input's value the way typing does, so a controlled caller's onChange sees it. */
function typeInto(el: HTMLInputElement, value: string) {
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/** A text input on the shadcn Input, with its label, help and error. Placeholders are example values, never the label. */
export function Input({ label, hint, error, icon, trailing, size = 'md', className, wrapperClassName, id: idProp, suggestions, ...rest }: InputProps) {
  // A caller's id names the control and the label's htmlFor points at it (it used to be spread
  // over the generated one, so the label pointed at nothing).
  return (
    <Field label={label} hint={hint} error={error} className={wrapperClassName} id={idProp}>
      {({ id, describedBy, invalid }) => {
        const props = {
          id,
          'aria-describedby': describedBy,
          'aria-invalid': invalid || undefined,
          className: cn(size === 'sm' && 'h-control-sm px-2 pointer-coarse:h-control', icon != null && (size === 'sm' ? 'pl-7' : 'pl-8'), trailing != null && 'pr-9', className),
          ...rest,
        };
        return (
          <div className="relative flex items-center">
            {icon && (
              <span className={cn('pointer-events-none absolute flex text-fg-subtle', size === 'sm' ? 'left-2 [&_svg]:size-3.5' : 'left-2.5 [&_svg]:size-4')}>{icon}</span>
            )}
            {suggestions ? <SuggestingInput suggestions={suggestions} {...props} /> : <InputPrimitive {...props} />}
            {trailing && <span className="absolute right-2 flex items-center text-fg-subtle">{trailing}</span>}
          </div>
        );
      }}
    </Field>
  );
}

/**
 * The input with its suggestion list: a combobox on a Radix Popover anchored to the field, at
 * the dropdown layer so it shows above a dialog. Focus stays in the input; arrows move, Enter
 * picks, Esc closes, and a click picks without blurring the field.
 */
function SuggestingInput({ suggestions, onFocus, onBlur, onChange, onKeyDown, ...rest }: ComponentProps<'input'> & { suggestions: readonly string[] }) {
  const ref = useRef<HTMLInputElement>(null);
  const listId = `${useId()}-list`;
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(String(rest.value ?? rest.defaultValue ?? ''));
  const [active, setActive] = useState(-1);
  const shown = useMemo(() => matchSuggestions(suggestions, typed), [suggestions, typed]);
  const expanded = open && shown.length > 0;
  const pick = (v: string) => {
    if (ref.current) typeInto(ref.current, v);
    setOpen(false);
    setActive(-1);
  };
  return (
    <Popover open={expanded} onOpenChange={(o) => !o && setOpen(false)}>
      <PopoverAnchor asChild>
        <InputPrimitive
          ref={ref}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          {...rest}
          onFocus={(e) => {
            setTyped(e.currentTarget.value);
            setOpen(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setOpen(false);
            setActive(-1);
            onBlur?.(e);
          }}
          onChange={(e) => {
            setTyped(e.currentTarget.value);
            setOpen(true);
            setActive(-1);
            onChange?.(e);
          }}
          onKeyDown={(e) => {
            if (expanded && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              const step = e.key === 'ArrowDown' ? 1 : -1;
              setActive((i) => (i + step + shown.length) % shown.length);
              return;
            }
            if (expanded && e.key === 'Enter' && active >= 0) {
              e.preventDefault();
              pick(shown[active]!);
              return;
            }
            if (expanded && e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              return;
            }
            onKeyDown?.(e);
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-(--radix-popover-trigger-width) p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (ref.current?.contains(e.target as Node)) e.preventDefault();
        }}
      >
        <div id={listId} role="listbox" className="flex max-h-(--radix-popover-content-available-height) flex-col overflow-y-auto">
          {shown.map((s, i) => (
            <div
              key={s}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              data-active={i === active || undefined}
              title={s}
              className="flex h-menu-item w-full shrink-0 cursor-default items-center rounded-control px-2 text-ui text-fg select-none hover:bg-fg/6 data-active:bg-fg/6"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
            >
              <span className="min-w-0 truncate">{s}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export interface TextareaProps extends ComponentProps<'textarea'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  wrapperClassName?: string;
}

/** A multi-line input on the shadcn Textarea. Ctrl+Enter submits from it. */
export function Textarea({ label, hint, error, className, wrapperClassName, rows = 4, id: idProp, ...rest }: TextareaProps) {
  return (
    <Field label={label} hint={hint} error={error} className={wrapperClassName} id={idProp}>
      {({ id, describedBy, invalid }) => (
        <TextareaPrimitive
          id={id}
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={cn('field-sizing-fixed resize-y', className)}
          {...rest}
        />
      )}
    </Field>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** A leading icon, shown in the list and in the closed field (an agent's glyph, a provider's mark). */
  icon?: ReactNode;
}

/** What one option shows: its label, after its icon when it has one. */
export function SelectOptionContent({ option: o }: { option: SelectOption }) {
  if (!o.icon) return <>{o.label}</>;
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-2 align-middle">
      {o.icon}
      <span className="min-w-0 truncate">{o.label}</span>
    </span>
  );
}

export interface SelectProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: SelectOption[];
  /** Shown while nothing is chosen. */
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Called with the chosen value as `e.target.value`, for code written against a native select. */
  onChange?: (e: { target: { value: string } }) => void;
  size?: 'sm' | 'md';
  disabled?: boolean;
  required?: boolean;
  /** Form field name: a hidden native select carries the value in form submissions. */
  name?: string;
  id?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  className?: string;
  wrapperClassName?: string;
}

/** Radix Select items cannot hold an empty string, which our "None" and "All" options use. */
const EMPTY = '__orgus_empty__';
const toItem = (v: string) => (v === '' ? EMPTY : v);
const fromItem = (v: string) => (v === EMPTY ? '' : v);

/**
 * Pick one of 6 to 15 known options, on the shadcn Select (a Radix listbox): type-ahead,
 * arrow keys and screen-reader behaviour from Radix. More than 15, async, or values people
 * type from memory: use a Combobox (Command in a Popover). Unavailable options stay visible
 * and disabled.
 */
export function Select({
  label,
  hint,
  error,
  options,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  onChange,
  size = 'md',
  disabled,
  required,
  name,
  id: idProp,
  autoFocus,
  'aria-label': ariaLabel,
  className,
  wrapperClassName,
}: SelectProps) {
  const hasEmpty = options.some((o) => o.value === '');
  const controlled = value === undefined ? undefined : value === '' && !hasEmpty ? '' : toItem(value);
  const initial = defaultValue === undefined ? undefined : toItem(defaultValue);
  return (
    <Field label={label} hint={hint} error={error} className={wrapperClassName} id={idProp}>
      {({ id, describedBy, invalid }) => (
        <SelectRoot
          value={controlled}
          defaultValue={initial}
          onValueChange={(v) => {
            const out = fromItem(v);
            onValueChange?.(out);
            onChange?.({ target: { value: out } });
          }}
          disabled={disabled}
          required={required}
          name={name}
        >
          <SelectTrigger
            id={id}
            size={size === 'sm' ? 'sm' : 'default'}
            aria-label={ariaLabel}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            autoFocus={autoFocus}
            className={cn('w-full', className)}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent position="popper" align="start">
            {options.map((o) => (
              <SelectItem key={o.value} value={toItem(o.value)} disabled={o.disabled} title={o.icon ? o.label : undefined}>
                <SelectOptionContent option={o} />
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      )}
    </Field>
  );
}
